/**
 * Full-case-viewer overlay shown before the rad clicks "Start case".
 *
 * Renders the DICOM grid + right panel dimmed underneath (the parent
 * still mounts them so the image is loaded and ready) but blocks
 * interaction. Centered card with arm/cohort labels, a pre-timed "Your
 * view" settings panel, and a prominent Start button. Pressing Space also
 * fires Start so the rad can begin keyboard-first.
 *
 * Pre-timed settings (#1): the reader picks their AI display mode, normal-
 * field visibility, and template here — before the timer starts — so the
 * setup time never lands in the active-reading measurement. These read/write
 * the same atoms the in-editor gear menu uses, so the choices carry into the
 * case (and across the session).
 *
 * Practice case (#4): when `isPractice`, the case is an untimed acclimation
 * run — the messaging says so and there is no timer. The reader gets
 * comfortable, signs when ready, and the timed cases follow.
 *
 * An on-demand "Instructions" button opens the reader-study participant
 * instructions (reader-time-study.md) via InstructionsDialog.
 *
 * Rendered by `LabelingPage` once study mode is active.
 */
import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { Link } from "react-router-dom";
import { InstructionsDialog } from "../labeling/InstructionsDialog";
import { ReporterModeToggle } from "../reporter/ReporterModeToggle";
import {
  reporterAiModeAtom,
  showNormalFieldsAtom,
  showLowConfidenceFindingsAtom,
} from "../../atoms/reporterPrefsAtoms";
import { FaFileAlt } from "../../utils/icons";

export interface StudyLockScreenProps {
  arm: "with_ai" | "without_ai";
  sessionOrder?: 1 | 2;
  /** Untimed acclimation case — no timer, "practice" messaging. */
  isPractice?: boolean;
  onStart: () => void;
  /** While true, DICOM metadata hasn't finished streaming. The lock-
   *  screen messaging reflects this honestly instead of falsely
   *  claiming "image is loaded". */
  imagesLoading?: boolean;
}

const ARM_TEMPLATE_LABEL: Record<StudyLockScreenProps["arm"], string> = {
  with_ai: "AI template",
  without_ai: "Blank template",
};

const READER_STUDY_INSTRUCTIONS_KEY = "reader-time-study.md";

/** One On/Off settings row, styled for the dark lock-screen card. */
function ToggleRow({ label, on, onToggle }: { label: string; on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm text-fg hover:bg-white/5"
    >
      <span>{label}</span>
      <span className={"text-xs font-semibold " + (on ? "text-signal-text-tint" : "text-fg-muted")}>
        {on ? "On" : "Off"}
      </span>
    </button>
  );
}

export const StudyLockScreen: React.FC<StudyLockScreenProps> = ({
  arm,
  sessionOrder,
  isPractice = false,
  onStart,
  imagesLoading = false,
}) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [aiMode, setAiMode] = useAtom(reporterAiModeAtom);
  const [showNormalFields, setShowNormalFields] = useAtom(showNormalFieldsAtom);
  const [showLowConfidence, setShowLowConfidence] = useAtom(showLowConfidenceFindingsAtom);

  // Space/Enter starts the case. Captured at document level so the rad
  // doesn't need to click into the panel first. Skip the keystroke if a
  // text input/contenteditable is focused (lock-screen blocks pointer
  // events, but a stray focused element from before the mount could
  // otherwise eat the keystroke). Suppressed when the instructions dialog is open.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (showInstructions) return;
      const active = document.activeElement as HTMLElement | null;
      if (active) {
        const tag = active.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || active.isContentEditable) return;
      }
      if (e.key === " " || e.code === "Space" || e.key === "Enter") {
        e.preventDefault();
        onStart();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onStart, showInstructions]);

  const withAi = arm === "with_ai";

  return (
    <div
      className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-[2px]"
      data-testid="study-lock-screen"
      role="dialog"
      aria-modal="true"
      aria-label={isPractice ? "Start practice case" : "Start case"}
    >
      <div className="bg-bg-2 border border-border-strong rounded shadow-2xl px-8 py-7 max-w-md w-full mx-4 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-signal-text-tint mb-2">
          {isPractice ? "Reader-time study · Practice" : "Reader-time study"}
        </p>
        <h2 className="text-2xl font-bold text-white mb-1">
          {imagesLoading ? "Loading images…" : isPractice ? "Practice case" : "Ready to read"}
        </h2>
        <p className="text-sm text-fg-muted mb-4">
          {isPractice
            ? "This case isn't timed. Get comfortable with the tools and set your view below, then sign when you're ready — the timed cases follow."
            : imagesLoading
              ? "Images are still streaming. The timer starts when you click Start."
              : "The image is loaded. The timer starts when you click Start."}
        </p>

        <div className="flex items-center justify-center gap-2 mb-4">
          <span
            className={
              "text-xs px-2 py-0.5 rounded-full border " +
              (withAi
                ? "border-signal-stroke bg-signal-tint text-signal-text-tint"
                : "border-border-strong bg-surface-2 text-fg-muted")
            }
          >
            {ARM_TEMPLATE_LABEL[arm]}
          </span>
          {sessionOrder !== undefined && (
            <span className="text-xs px-2 py-0.5 rounded-full border border-border-strong text-fg-muted">
              Session {sessionOrder}
            </span>
          )}
        </div>

        {/* Pre-timed view settings (#1) — set before Start so config time
            isn't measured. Wired to the same atoms as the in-editor gear. The
            AI-suggestions mode shows on both arms so a reader can set it before
            the timed AI section even while on a blank case. */}
        <div className="mb-6 rounded-lg border border-border bg-black/20 p-3 text-left">
          <p className="text-xs font-semibold uppercase tracking-wider text-fg-muted mb-2">
            Your view
          </p>
          <div className="mb-2">
            <p className="text-xs text-fg-muted mb-1">AI suggestions</p>
            <ReporterModeToggle mode={aiMode} onChange={setAiMode} />
          </div>
          <ToggleRow
            label="Show normal fields"
            on={showNormalFields}
            onToggle={() => setShowNormalFields((v) => !v)}
          />
          <ToggleRow
            label="Show review-tier findings"
            on={showLowConfidence}
            onToggle={() => setShowLowConfidence((v) => !v)}
          />
          <Link
            to="/my-templates"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 flex items-center gap-1.5 rounded px-2 py-1.5 text-sm text-signal-text-tint hover:bg-white/5"
          >
            <FaFileAlt className="w-3 h-3" />
            <span>Edit your template</span>
          </Link>
        </div>

        <button
          type="button"
          onClick={onStart}
          autoFocus
          className="w-full bg-signal hover:bg-signal/90 text-white font-medium px-5 py-3 rounded-lg transition-colors"
          data-testid="study-lock-screen-start"
        >
          {isPractice ? "Start practice case" : "Start case"}
        </button>
        <p className="text-xs text-fg-muted mt-3">
          Press{" "}
          <kbd className="font-mono bg-surface-2 border border-border-strong rounded px-1.5 py-0.5">
            Space
          </kbd>{" "}
          to start
        </p>

        <div className="mt-4 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => setShowInstructions(true)}
            className="flex items-center justify-center gap-1.5 w-full text-xs text-fg-muted hover:text-white transition-colors py-1"
            data-testid="study-lock-screen-instructions"
          >
            <FaFileAlt className="w-3 h-3" />
            <span>Instructions</span>
          </button>
        </div>
      </div>

      <InstructionsDialog
        instructionsKey={READER_STUDY_INSTRUCTIONS_KEY}
        title="Study instructions"
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
      />
    </div>
  );
};
